import React, { useState, useEffect } from 'react'
import { 
  Brain, AlertTriangle, RefreshCw, CheckCircle, XCircle,
  FileText, Copy, Edit3, Sparkles, BookOpen, Info
} from 'lucide-react'
import { toast } from 'sonner'

const API_URL = process.env.REACT_APP_BACKEND_URL

const DISCLAIMER = "⚠️ Analyse automatique fournie à titre indicatif. La décision finale appartient au gestionnaire."

export default function AnalyseIABlock({ sinistreId, tenantId, onAnalyseComplete, useKB = true }) {
  const [loading, setLoading] = useState(false)
  const [analyse, setAnalyse] = useState(null)
  const [error, setError] = useState(null)
  const [showSources, setShowSources] = useState(false)

  useEffect(() => {
    fetchExistingAnalyse()
  }, [sinistreId])

  async function fetchExistingAnalyse() {
    try {
      const res = await fetch(`${API_URL}/api/ia/analyses/${sinistreId}`)
      const data = await res.json()
      
      if (data.analyses?.length > 0) {
        // Get the most recent pre_analyse (KB or standard)
        const preAnalyse = data.analyses.find(a => 
          a.type_analyse === 'pre_analyse_kb' || a.type_analyse === 'pre_analyse_couverture'
        )
        if (preAnalyse) {
          setAnalyse(preAnalyse.resultat || preAnalyse)
        }
      }
    } catch (err) {
      console.error('Error fetching analyses:', err)
    }
  }

  async function lancerAnalyse() {
    setLoading(true)
    setError(null)
    
    try {
      // Utiliser la nouvelle API KB si disponible
      const endpoint = useKB 
        ? `${API_URL}/api/ia/analyser-sinistre-kb/${sinistreId}?pour_declarant=false&sauvegarder=true`
        : `${API_URL}/api/ia/pre-analyser-sinistre`
      
      const options = useKB 
        ? { method: 'POST' }
        : {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sinistre_id: sinistreId, pour_declarant: false })
          }
      
      const res = await fetch(endpoint, options)
      const data = await res.json()
      
      if (data.erreur || data.detail) {
        setError(data.erreur || data.detail)
        toast.error('Erreur lors de l\'analyse')
      } else {
        setAnalyse(data)
        toast.success('Analyse IA générée')
        onAnalyseComplete?.()
      }
    } catch (err) {
      setError('Erreur de connexion au service IA')
      toast.error('Erreur de connexion')
    } finally {
      setLoading(false)
    }
  }

  function getProbabiliteColor(prob) {
    if (prob >= 0.7) return 'text-emerald-600 bg-emerald-50'
    if (prob >= 0.4) return 'text-amber-600 bg-amber-50'
    return 'text-red-600 bg-red-50'
  }

  function formatCurrency(amount) {
    if (amount == null) return '—'
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount)
  }

  // Extraire la garantie (nouveau format ou ancien)
  const garantie = analyse?.garantie_probable?.libelle || analyse?.garantie_probable || null
  const franchise = analyse?.franchise_estimee?.montant ?? analyse?.franchise_estimee ?? null
  const plafond = analyse?.plafond_probable?.montant ?? analyse?.plafond_probable ?? null
  
  // Sources KB
  const sourcesKB = analyse?._sources_tracabilite || analyse?.sources_utilisees

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="analyse-ia-block">
      <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-violet-50 to-slate-50 flex items-center justify-between">
        <h2 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
          <Brain size={16} className="text-violet-600" />
          Analyse IA
          {sourcesKB && (
            <span className="text-xs font-normal text-violet-500 bg-violet-100 px-1.5 py-0.5 rounded">
              KB
            </span>
          )}
        </h2>
        <button
          onClick={lancerAnalyse}
          disabled={loading}
          data-testid="lancer-analyse-btn"
          className="px-3 py-1.5 bg-violet-600 text-white text-xs font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
        >
          {loading ? (
            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <RefreshCw size={12} />
          )}
          {analyse ? 'Relancer' : 'Analyser'}
        </button>
      </div>

      <div className="p-5">
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 mb-4">
            <AlertTriangle size={16} />
            {error}
          </div>
        )}

        {!analyse && !loading && !error && (
          <div className="text-center py-6">
            <Sparkles size={32} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-400">Aucune analyse disponible</p>
            <p className="text-xs text-slate-400 mt-1">Cliquez sur "Analyser" pour générer une pré-analyse</p>
          </div>
        )}

        {loading && (
          <div className="text-center py-6">
            <div className="w-10 h-10 border-2 border-violet-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-slate-500">Analyse en cours...</p>
            <p className="text-xs text-slate-400 mt-1">Le modèle IA analyse le sinistre et les documents KB</p>
          </div>
        )}

        {analyse && !loading && (
          <div className="space-y-4">
            {/* Probabilité de couverture */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Probabilité de couverture</span>
              <span className={`text-lg font-bold px-3 py-1 rounded-lg ${getProbabiliteColor(analyse.probabilite_couverture)}`}>
                {(analyse.probabilite_couverture * 100).toFixed(0)}%
              </span>
            </div>

            {/* Garantie / Franchise / Plafond */}
            <div className="grid grid-cols-3 gap-3 pt-3 border-t border-slate-100">
              <div>
                <p className="text-xs text-slate-400 mb-1">Garantie probable</p>
                <p className="text-sm font-medium text-slate-800">{garantie || '—'}</p>
                {analyse?.garantie_probable?.source_document && (
                  <p className="text-xs text-slate-400 mt-0.5 truncate" title={analyse.garantie_probable.source_document}>
                    Source: {analyse.garantie_probable.source_document}
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-1">Franchise estimée</p>
                <p className="text-sm font-medium text-slate-800">{formatCurrency(franchise)}</p>
                {analyse?.franchise_estimee?.source_document && (
                  <p className="text-xs text-slate-400 mt-0.5 truncate">
                    Source: {analyse.franchise_estimee.source_document}
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-1">Plafond probable</p>
                <p className="text-sm font-medium text-slate-800">{formatCurrency(plafond)}</p>
              </div>
            </div>

            {/* Exclusions potentielles */}
            {analyse.exclusions_potentielles?.length > 0 && (
              <div className="pt-3 border-t border-slate-100">
                <p className="text-xs text-slate-400 mb-2">Exclusions potentielles</p>
                <ul className="space-y-1">
                  {analyse.exclusions_potentielles.map((excl, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-orange-700 bg-orange-50 px-2 py-1 rounded">
                      <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                      {typeof excl === 'string' ? excl : excl.libelle || excl.raison}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Synthèse gestionnaire */}
            {analyse.synthese_gestionnaire && (
              <div className="pt-3 border-t border-slate-100">
                <p className="text-xs text-slate-400 mb-2">Synthèse pour le gestionnaire</p>
                <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg">
                  {analyse.synthese_gestionnaire}
                </p>
              </div>
            )}

            {/* Sources documentaires KB */}
            {sourcesKB && (
              <div className="pt-3 border-t border-slate-100">
                <button 
                  onClick={() => setShowSources(!showSources)}
                  className="flex items-center gap-2 text-xs text-slate-500 hover:text-violet-600"
                >
                  <BookOpen size={14} />
                  Sources documentaires ({sourcesKB.documents_contrat?.length || 0} documents)
                  <Info size={12} />
                </button>
                
                {showSources && (
                  <div className="mt-2 p-3 bg-slate-50 rounded-lg space-y-2">
                    {sourcesKB.documents_contrat?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-slate-600 mb-1">Documents contrat utilisés:</p>
                        <ul className="space-y-1">
                          {sourcesKB.documents_contrat.map((doc, i) => (
                            <li key={i} className="text-xs text-slate-500 flex items-center gap-1">
                              <FileText size={10} />
                              {doc.nom || doc.nom_affichage} 
                              {doc.version && <span className="text-slate-400">v{doc.version}</span>}
                              {doc.categorie && <span className="text-violet-500">({doc.categorie})</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {sourcesKB.tenant_settings_snapshot && (
                      <div>
                        <p className="text-xs font-medium text-slate-600 mb-1">Paramètres tenant:</p>
                        <p className="text-xs text-slate-500">
                          Seuil rétention: {formatCurrency(sourcesKB.tenant_settings_snapshot.seuil_conservation)}
                          {sourcesKB.tenant_settings_snapshot.doctrine_personnalisee && 
                            <span className="ml-2 text-violet-500">(Doctrine personnalisée)</span>
                          }
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Commentaire de prudence */}
            {analyse.commentaire_prudence && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">{analyse.commentaire_prudence}</p>
              </div>
            )}

            {/* Disclaimer */}
            <div className="text-xs text-slate-400 italic pt-3 border-t border-slate-100">
              {DISCLAIMER}
            </div>

            {/* Metadata */}
            <div className="flex items-center justify-between text-xs text-slate-400 pt-2">
              <span>Modèle: {analyse.modele_ia}</span>
              <span>Confiance: {analyse.niveau_confiance}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
